import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const CurrencyContext = createContext(null);
export const useCurrency = () => useContext(CurrencyContext);

const SYMBOLS = { INR: "₹", USD: "$", GBP: "£", EUR: "€", AED: "AED ", SGD: "S$" };
const LOCALES = { INR: "en-IN", USD: "en-US", GBP: "en-GB", EUR: "en-DE", AED: "en-AE", SGD: "en-SG" };
const SUPPORTED = ["INR", "USD", "GBP", "EUR", "AED", "SGD"];

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => localStorage.getItem("yt-cur") || "INR");
  const [rates, setRates] = useState({ INR: 1 });

  useEffect(() => {
    api.get("/fx/rates").then((r) => setRates(r.data.rates)).catch(() => {});
  }, []);

  const setCurrency = useCallback((c) => {
    if (!SUPPORTED.includes(c)) return;
    setCurrencyState(c);
    localStorage.setItem("yt-cur", c);
  }, []);

  const convert = useCallback((inr) => {
    const r = rates[currency] || 1;
    return inr * r;
  }, [currency, rates]);

  const format = useCallback((inr, opts = {}) => {
    const amount = convert(inr);
    const symbol = SYMBOLS[currency] || "";
    if (currency === "INR") {
      return `${symbol}${Math.round(amount).toLocaleString("en-IN")}`;
    }
    // Round smartly
    const rounded = amount >= 100 ? Math.round(amount) : Math.round(amount * 100) / 100;
    const formatted = rounded.toLocaleString(LOCALES[currency] || "en-US", {
      minimumFractionDigits: rounded % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2,
    });
    return `${symbol}${formatted}`;
  }, [currency, convert]);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, rates, convert, format, supported: SUPPORTED, symbols: SYMBOLS }}>
      {children}
    </CurrencyContext.Provider>
  );
}
